if (!customElements.get('wao-hero-slider')) {
  customElements.define('wao-hero-slider', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const on = (target, event, handler) => target.addEventListener(event, handler, { signal: this.abort.signal });
      this.slides = [...this.querySelectorAll('.wao-hero__slide')];
      this.dots = [...this.querySelectorAll('[data-slide]')];
      this.viewport = this.querySelector('.wao-hero__viewport');
      this.index = 0;
      this.slides.forEach((slide, index) => {
        slide.classList.remove('is-animating');
        slide.classList.toggle('is-active', index === 0);
        slide.inert = index !== 0;
        if (index) slide.setAttribute('aria-hidden', 'true'); else slide.removeAttribute('aria-hidden');
      });
      this.dots.forEach((dot, index) => { if (!index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
      this.busy = false;
      this.animations = [];
      this.pauses = new Set();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.playing = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
      this.pauseButton = this.querySelector('[data-pause]');
      on(this, 'click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-next')) this.move(1, true);
        if (button.hasAttribute('data-previous')) this.move(-1, true);
        if (button.hasAttribute('data-slide')) this.go(Number(button.dataset.slide), 1, true);
        if (button.hasAttribute('data-pause')) {
          this.playing = !this.playing;
          this.updatePauseButton();
          this.schedule();
        }
      });
      on(this, 'keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1, true);
        }
      });
      on(this, 'mouseenter', () => this.pause('hover', true));
      on(this, 'mouseleave', () => this.pause('hover', false));
      on(this, 'focusin', () => this.pause('focus', true));
      on(this, 'focusout', (event) => { if (!this.contains(event.relatedTarget)) this.pause('focus', false); });
      on(document, 'visibilitychange', () => this.pause('hidden', document.hidden));
      on(this.reducedMotion, 'change', () => {
        if (this.reducedMotion.matches) this.playing = false;
        this.updatePauseButton();
        this.schedule();
      });
      on(this, 'pointerdown', (event) => { if (event.pointerType !== 'mouse') this.touch = { x: event.clientX, y: event.clientY }; });
      on(this, 'pointerup', (event) => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch.x, dy = event.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.suppressClick = true;
          this.move(dx < 0 ? 1 : -1, true);
          clearTimeout(this.clickTimer);
          this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 500);
        }
      });
      on(this, 'pointercancel', () => { this.touch = null; });
      this.addEventListener('click', (event) => { if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); this.suppressClick = false; } }, { capture: true, signal: this.abort.signal });
      on(document, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index >= 0) { this.pause('editor', true); this.go(index, 1, true, true); }
      });
      on(document, 'shopify:block:deselect', (event) => {
        if (this.slides.some(slide => slide.dataset.blockId === event.detail.blockId)) this.pause('editor', false);
      });
      this.pause('hidden', document.hidden);
      this.updatePauseButton();
      this.schedule();
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.clickTimer);
      this.abort?.abort();
      this.animations?.forEach(animation => animation.cancel());
    }
    updatePauseButton() {
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-label', this.playing ? 'Pause autoplay' : 'Play autoplay');
      this.pauseButton.firstElementChild.textContent = this.playing ? '❚❚' : '▶';
    }
    pause(reason, paused) {
      if (paused) this.pauses.add(reason); else this.pauses.delete(reason);
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.playing && !this.busy && !this.pauses.size && this.slides.length > 1) {
        this.timer = setTimeout(() => this.move(1), Number(this.dataset.interval));
      }
    }
    move(direction, manual = false) {
      if (this.slides.length < 2) return;
      this.go((this.index + direction + this.slides.length) % this.slides.length, direction, manual);
    }
    async go(next, direction = 1, manual = false, instant = false) {
      if (next === this.index || this.busy || !this.slides[next]) return;
      clearTimeout(this.timer);
      this.busy = true;
      const lifecycle = this.abort.signal;
      const outgoing = this.slides[this.index], incoming = this.slides[next];
      outgoing.setAttribute('aria-hidden', 'true'); outgoing.inert = true;
      incoming.removeAttribute('aria-hidden'); incoming.inert = false;
      outgoing.classList.add('is-animating');
      incoming.classList.add('is-active');
      outgoing.classList.remove('is-active');
      this.index = next;
      this.dots.forEach((dot, index) => { if (index === next) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
      this.viewport.setAttribute('aria-live', manual ? 'polite' : 'off');
      const duration = instant || this.reducedMotion.matches ? 0 : Number(this.dataset.speed);
      if (duration && incoming.animate) {
        const sign = (this.dataset.effect === 'slide-right' ? -1 : 1) * direction;
        const frames = this.dataset.effect === 'fade'
          ? [[{ opacity: 1 }, { opacity: 0 }], [{ opacity: 0 }, { opacity: 1 }]]
          : [[{ transform: 'translateX(0)', opacity: 1 }, { transform: `translateX(${-sign * 100}%)`, opacity: 1 }], [{ transform: `translateX(${sign * 100}%)` }, { transform: 'translateX(0)' }]];
        this.animations = [outgoing.animate(frames[0], { duration, easing: 'ease-in-out' }), incoming.animate(frames[1], { duration, easing: 'ease-in-out' })];
        await Promise.allSettled(this.animations.map(animation => animation.finished));
      }
      if (lifecycle.aborted) return;
      outgoing.classList.remove('is-animating');
      this.animations = [];
      this.busy = false;
      if (this.isConnected) this.schedule();
    }
  });
}
